import React from "react";
import {
	Button,
	ButtonProps,
	ClassNameProps,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	StyleProps,
} from "@hex-insights/core";
import { Contract, Student } from "../../../../Utilities";
import { PaymentForm } from "../../../Forms";
import styles from "./styles.module.css";

export type PaymentCreateModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<PaymentCreateModalProps, "studentID" | "contractID" | "formStateArgs" | "onSuccess"> &
	Partial<ClassNameProps & StyleProps>;

export function PaymentCreateModalController({
	studentID,
	contractID,
	formStateArgs,
	onSuccess,
	...props
}: PaymentCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} {...props} />

			<Modal.If condition={isModalOpen}>
				<PaymentCreateModal
					studentID={studentID}
					contractID={contractID}
					formStateArgs={formStateArgs}
					onSuccess={onSuccess}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

export type PaymentCreateModalProps = {
	studentID: Student["id"];
	contractID?: Contract["id"];
} & Partial<Pick<PaymentForm.ControlledCreateProps, "formStateArgs" | "onSuccess">> &
	RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

export function PaymentCreateModal({
	studentID,
	contractID,
	formStateArgs,
	onSuccess,
	ifRef,
	onClose,
	className,
	style,
}: PaymentCreateModalProps) {
	const fullOnSuccess = React.useCallback<PaymentForm.ControlledCreateProps["onSuccess"]>(
		(...args) => {
			onSuccess?.(...args);
			onClose();
		},
		[onSuccess, onClose],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>New Payment</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<PaymentForm.ControlledCreate
					studentID={studentID}
					contractID={contractID}
					formStateArgs={formStateArgs}
					formNameSuffix={studentID}
					onSuccess={fullOnSuccess}
				/>
			</Modal.Body>
		</Modal>
	);
}
